import { FanResponseDto, OfListsService, type OnlyFansList } from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { updateLists } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { handleError } from '@/utils/error'

import { listsService } from '../service/lists'
import type { UpdateListsUserRequestOptions } from '../types'

export const useManageUserLists = () => {
  const creatorId = useUnit($creatorId)
  const toast = useToast()
  const queryClient = useQueryClient()

  const updateListsStatesMutation = useMutation(
    ({
      remove = [],
      add = [],
      userId,
    }: Partial<Record<'remove' | 'add', OnlyFansList[]>> & {
      userId: FanResponseDto['id']
    }) => {
      const request: UpdateListsUserRequestOptions = {
        data: [
          // eslint-disable-next-line no-unsafe-optional-chaining
          ...remove?.map(({ id }) => {
            return { listId: Number(id), add: false }
          }),
          // eslint-disable-next-line no-unsafe-optional-chaining
          ...add?.map(({ id }) => {
            return { listId: Number(id), add: true }
          }),
        ],
      }

      return listsService.updateUserInLists({
        creatorId: creatorId!,
        userId,
        request,
      })
    },
    {
      onSuccess: async (_, { remove = [], add = [], userId }) => {
        updateLists({ fanId: userId, remove, add })

        await Promise.allSettled([
          queryClient.invalidateQueries('all-lists'),
          queryClient.invalidateQueries('pinned-lists'),
          queryClient.invalidateQueries('all-lists-extended'),
        ])
        toast.success('Successfully updated user lists')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while updating lists')
      },
    },
  )

  const createUserListMutation = useMutation(
    ({
      name,
      forUserId,
    }: {
      name: FanResponseDto['name']
      forUserId?: FanResponseDto['id']
    }) =>
      OfListsService.createList({ name, creatorId: creatorId! }).then(
        async (list) => {
          if (forUserId) {
            await updateListsStatesMutation.mutateAsync({
              add: [list],
              userId: forUserId,
            })
          }

          return list
        },
      ),
    {
      onSuccess: async () => {
        await Promise.allSettled([
          queryClient.invalidateQueries('all-lists'),
          queryClient.invalidateQueries('all-lists-extended'),
        ])
        toast.success('List successfully created')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while creating list')
      },
    },
  )

  return {
    updateListsStatesMutation,
    createUserListMutation,
  }
}
